import React, { useState } from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import clsx from "clsx";
import { useFormik } from "formik";
import * as auth from "../redux/AuthRedux";
import { login, LOGIN_URL } from "../redux/AuthCRUD";
import { AxiosError } from "axios";
import { ErrorResponse } from "../../../support/utils";
import { Link, useNavigate, useLocation } from "react-router-dom";

function logLoginError(err: AxiosError<ErrorResponse>): void {
	const url = err.config?.url ? `${err.config.baseURL || ""}${err.config.url}` : LOGIN_URL;
	const payload = {
		loginError: true,
		requestUrl: url,
		status: err.response?.status,
		statusText: err.response?.statusText,
		responseData: err.response?.data,
		message: err.message,
		code: err.code,
	};
	console.error("[Login] Request failed. Full details:", payload);
	if (err.response?.data?.errors && typeof err.response.data.errors === "object") {
		console.error("[Login] Validation/error details from API:", err.response.data.errors);
	}
}

function getLoginErrorMessage(err: AxiosError<ErrorResponse>): string {
	logLoginError(err);

	const data = err.response?.data;
	const apiMessage = data?.message;
	// Laravel validation: errors.email[0] often has the real message (e.g. "These credentials do not match our records.")
	const errors = data?.errors as Record<string, string[] | undefined> | undefined;
	const firstError = errors?.email?.[0] ?? (errors && Object.values(errors)[0]?.[0]);

	if (firstError) return firstError;
	if (apiMessage) return apiMessage;

	if (err.response) {
		const status = err.response.status;
		if (status === 401) return "Invalid email or password.";
		if (status === 404) return "Login endpoint not found. The app may be pointing to the wrong API URL.";
		if (status === 422) return "Validation failed. Check the browser console (F12) for details.";
		if (status >= 500) return "Server error. Please try again later.";
		return `Request failed (${status}). Check the browser console (F12) for details.`;
	}
	if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
		return "Cannot reach the API (network or CORS). Check the browser console (F12) and that the API URL is correct.";
	}
	return err.message || "Login failed. Check the browser console (F12) for details.";
}

const loginSchema = Yup.object().shape({
	email: Yup.string()
		.email("Wrong email format")
		.min(3, "Minimum 3 symbols")
		.max(50, "Maximum 50 symbols")
		.required("Email is required"),
	password: Yup.string()
		.min(3, "Minimum 3 symbols")
		.max(50, "Maximum 50 symbols")
		.required("Password is required"),
});

const initialValues = {
	email: "",
	password: "",
};

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function Login() {
	const location = useLocation();
	const navigate = useNavigate();

	const [loading, setLoading] = useState(false);
	const dispatch = useDispatch();
	const formik = useFormik({
		initialValues,
		validationSchema: loginSchema,
		onSubmit: async (values, { setStatus, setSubmitting }) => {
			setSubmitting(true);
			setLoading(true);
			try {
				const response = await login(values.email, values.password);
				// Backend may return { access_token } or { value: { access_token } }
				const data = response.data as unknown as Record<string, unknown>;
				const payload = (data.value as Record<string, unknown>) ?? data;
				const token = (payload.access_token as string) ?? (data.access_token as string);
				const status = (payload.status as string) ?? (data.status as string);
				const email = (payload.email as string) ?? (data.email as string);

				if (status === "ENROLL_2FA") {
					// Approved account, first login: force 2FA enrolment. Carry the
					// password in-memory via router state — never persisted.
					navigate(`/auth/enroll/${email}`, {
						state: { password: values.password },
					});
				} else if (status === "PARTIAL") {
					// The single-use challenge proves the password step passed;
					// /auth/otp rejects the code without it. In-memory router
					// state only — never persisted.
					const challenge = (payload.challenge as string) ?? (data.challenge as string);
					navigate(`${location.pathname}/partial/${email}`, {
						state: { challenge },
					});
				} else if (token) {
					dispatch(auth.actions.login(token));
				} else {
					setStatus("Invalid response from server");
				}
				setLoading(false);
				setSubmitting(false);
			} catch (error) {
				const err = error as AxiosError<ErrorResponse>;
				setLoading(false);
				setSubmitting(false);
				setStatus(getLoginErrorMessage(err));
			}

			// setLoading(true);
			// setTimeout(() => {
			//   login(values.email, values.password)
			//     .then(({ data: { accessToken } }) => {
			//       setLoading(false);
			//       dispatch(auth.actions.login(accessToken));
			//     })
			//     .catch(() => {
			//       setLoading(false);
			//       setSubmitting(false);
			//       setStatus("The login detail is incorrect");
			//     });
			// }, 1000);
		},
	});

	return (
		<form
			className="form w-100"
			onSubmit={formik.handleSubmit}
			noValidate
			id="kt_login_signin_form"
		>
			{/* begin::Title */}
			<div className="pb-lg-15">
				<h3 className="fw-bolder text-dark display-6">
					Welcome to Verbonden
				</h3>
				<div className="text-muted fw-bold fs-3">
					New Here?{" "}
					<Link
						to="/auth/register"
						className="text-primary fw-bolder"
						id="kt_login_signin_form_singup_button"
					>
						Create Account
					</Link>
				</div>
			</div>
			{/* begin::Title */}

			{formik.status && (
				<div className="mb-lg-15 alert alert-danger">
					<div className="alert-text font-weight-bold">
						{formik.status}
					</div>
				</div>
			)}

			{/* {formik.status ? (
				<div className="mb-lg-15 alert alert-danger">
					<div className="alert-text font-weight-bold">
						{formik.status}
					</div>
				</div>
			) : (
				<div className="mb-lg-15 alert alert-info">
					<div className="alert-text ">
						Use credentials <strong>admin@demo.com</strong> and{" "}
						<strong>demo</strong> to sign in.
					</div>
				</div>
			)} */}

			{/* begin::Form group */}
			<div className="v-row mb-10 fv-plugins-icon-container">
				<label className="form-label fs-6 fw-bolder text-dark">
					Email
				</label>
				<input
					placeholder="Email"
					{...formik.getFieldProps("email")}
					className={clsx(
						"form-control form-control-lg form-control-solid",
						{
							"is-invalid":
								formik.touched.email && formik.errors.email,
						},
						{
							"is-valid":
								formik.touched.email && !formik.errors.email,
						}
					)}
					type="email"
					name="email"
					autoComplete="off"
				/>
				{formik.touched.email && formik.errors.email && (
					<div className="fv-plugins-message-container">
						<div className="fv-help-block">
							{formik.errors.email}
						</div>
					</div>
				)}
			</div>
			{/* end::Form group */}

			{/* begin::Form group */}
			<div className="fv-row mb-10 fv-plugins-icon-container">
				<div className="d-flex justify-content-between mt-n5">
					<label className="form-label fs-6 fw-bolder text-dark pt-5">
						Password
					</label>

					<Link
						to="/auth/forgot-password"
						className="text-primary fs-6 fw-bolder text-hover-primary pt-5"
						id="kt_login_signin_form_password_reset_button"
					>
						Forgot Password ?
					</Link>
				</div>
				<input
					placeholder="Password"
					type="password"
					autoComplete="off"
					{...formik.getFieldProps("password")}
					className={clsx(
						"form-control form-control-lg form-control-solid",
						{
							"is-invalid":
								formik.touched.password &&
								formik.errors.password,
						},
						{
							"is-valid":
								formik.touched.password &&
								!formik.errors.password,
						}
					)}
				/>
				{formik.touched.password && formik.errors.password && (
					<div className="fv-plugins-message-container">
						<div className="fv-help-block">
							{formik.errors.password}
						</div>
					</div>
				)}
			</div>
			{/* end::Form group */}

			{/* begin::Action */}
			<div className="pb-lg-0 pb-5">
				<button
					type="submit"
					id="kt_login_signin_form_submit_button"
					className="btn btn-primary fw-bolder fs-6 px-8 py-4 my-3 me-3"
					disabled={formik.isSubmitting || !formik.isValid}
				>
					{!loading && (
						<span className="indicator-label">Sign In</span>
					)}
					{loading && (
						<span
							className="indicator-progress"
							style={{ display: "block" }}
						>
							Please wait...{" "}
							<span className="spinner-border spinner-border-sm align-middle ms-2"></span>
						</span>
					)}
				</button>
				{/* <button
					type="button"
					className="btn btn-light-primary fw-bolder px-8 py-4 my-3 fs-6 mr-3"
				>
					<img
						src={toAbsoluteUrl(
							"/media/svg/brand-logos/google-icon.svg"
						)}
						className="w-20px h-20px me-3"
						alt=""
					/>
					Sign in with Google
				</button> */}
			</div>
			{/* end::Action */}
		</form>
	);
}
